<template>
  <section 
    id="sessions" 
    class="w-full relative overflow-hidden font-poppins bg-[#FAF9F7]"
    style="position: relative; z-index: 6;"
  >
    <!-- Simplified transition with reduced visual noise -->
    <div class="relative py-16 md:py-20">
      <!-- Visual connector to TherapyRoom section - kept subtle -->
      <div class="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-[#FAF9F7] via-[#FAF9F7]/70 to-transparent"></div>
      
      <!-- Simplified background with very subtle gradient -->
      <div class="absolute inset-0 bg-gradient-to-br from-[#FCECEC]/5 to-[#FAF9F7]"></div>
      <!-- Background pattern reduced to 5% opacity for subtle texture -->
      <div class="absolute inset-0 opacity-5" style="background-image: url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23D2919A\' fill-opacity=\'0.3\'%3E%3Cpath d=\'M36 34v6h6v-6h-6zm6 6v6h-6v-6h6zm-6-6v-6h6v6h-6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')"></div>
      
      <!-- Decorative elements with reduced opacity -->
      <div class="absolute left-0 top-40 w-40 h-40 bg-[#D2919A]/3 rounded-full blur-3xl"></div>
      <div class="absolute right-0 bottom-20 w-60 h-60 bg-[#D2919A]/3 rounded-full blur-3xl"></div>
      
      <div class="container mx-auto px-4 md:px-8 lg:px-16 max-w-[1600px] relative z-10">
        <!-- Enhanced section header with centered styling to match other sections -->
        <div class="mb-10 md:mb-16 text-center">
          <h2 class="text-4xl md:text-5xl font-bold text-[#333333] mb-4">Sessions</h2>
          <div class="h-1 w-24 mx-auto bg-[#D2919A] rounded-full"></div>
          
          <p class="text-[#333333] mt-6 text-lg max-w-3xl mx-auto leading-relaxed">
            I offer flexible counselling options to suit your needs and preferences.
          </p>
        </div>
        
        <!-- Sessions information - Redesigned with better visual separation -->
        <div class="mb-16 max-w-4xl mx-auto bg-white/90 rounded-xl p-8 shadow-sm border border-[#D2919A]/10">
          <!-- Main info with enhanced typography and better readability -->
          <div class="space-y-10">
            <!-- Person-Centered Approach - Improved as clear subheading -->
            <div>
              <h3 class="text-2xl font-bold text-[#D2919A] mb-6 text-center">
                A Person-Centered Approach
              </h3>
              <p class="text-[#333333] leading-relaxed text-center mb-8 max-w-2xl mx-auto">
                My sessions are tailored to your needs. Maybe that sounds like a marketing buzzword, but as someone who believes in a flexible, person-centered approach, I try to meet you where you're at.
              </p>
              
              <!-- Subtle divider -->
              <div class="w-full h-px bg-[#D2919A]/10 my-8"></div>
            </div>
            
            <!-- Session Key Information in 2x2 Grid - Updated styling -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">
              <!-- Session Duration -->
              <div class="flex items-start gap-4 p-4 bg-white rounded-lg border border-[#D2919A]/10 hover:border-[#D2919A]/20 hover:shadow-sm transition-all duration-300">
                <div class="w-12 h-12 rounded-full bg-[#D2919A]/5 flex items-center justify-center flex-shrink-0 group-hover:bg-[#D2919A]/10 transition-all duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-[#D2919A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 class="text-xl font-semibold text-[#333333]">50-minute sessions</h4>
                  <p class="text-[#333333]/80 text-sm mt-1">Sessions are carefully timed to provide optimal therapeutic benefit</p>
                </div>
              </div>
              
              <!-- Session Cost -->
              <div class="flex items-start gap-4 p-4 bg-white rounded-lg border border-[#D2919A]/10 hover:border-[#D2919A]/20 hover:shadow-sm transition-all duration-300">
                <div class="w-12 h-12 rounded-full bg-[#D2919A]/5 flex items-center justify-center flex-shrink-0 group-hover:bg-[#D2919A]/10 transition-all duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-[#D2919A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h4 class="text-xl font-semibold text-[#333333]">£50 per session</h4>
                  <p class="text-[#333333]/80 text-sm mt-1">Transparent pricing with no hidden fees</p>
                </div>
              </div>
              
              <!-- Free Consultation -->
              <div class="flex items-start gap-4 p-4 bg-white rounded-lg border border-[#D2919A]/10 hover:border-[#D2919A]/20 hover:shadow-sm transition-all duration-300">
                <div class="w-12 h-12 rounded-full bg-[#D2919A]/5 flex items-center justify-center flex-shrink-0 group-hover:bg-[#D2919A]/10 transition-all duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-[#D2919A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <h4 class="text-xl font-semibold text-[#333333]">Free initial call</h4>
                  <p class="text-[#333333]/80 text-sm mt-1">15-minute phone consultation to discuss your needs</p>
                </div>
              </div>
              
              <!-- Payment Terms -->
              <div class="flex items-start gap-4 p-4 bg-white rounded-lg border border-[#D2919A]/10 hover:border-[#D2919A]/20 hover:shadow-sm transition-all duration-300">
                <div class="w-12 h-12 rounded-full bg-[#D2919A]/5 flex items-center justify-center flex-shrink-0 group-hover:bg-[#D2919A]/10 transition-all duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-[#D2919A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 class="text-xl font-semibold text-[#333333]">Payable in advance</h4>
                  <p class="text-[#333333]/80 text-sm mt-1">Secure your appointment with advance payment</p>
                </div>
              </div>
            </div>
            
            <!-- Call to action - Centered for design consistency -->
            <div class="mt-12 flex justify-center">
              <a href="#contact" @click.prevent="scrollToContact" 
                class="primary-button inline-flex items-center px-8 py-4 rounded-full bg-[#D2919A] text-white font-medium shadow-sm transform transition-all duration-300 hover:bg-[#D2919A]/90">
                Book Your Session
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </a>
            </div>
          </div>
        </div>
        
        <!-- Visual section divider - made more subtle -->
        <div class="w-full h-px bg-[#D2919A]/10 my-12"></div>
        
        <!-- Session Types - Clean layout with subtle separation -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-y-8 gap-x-8 lg:gap-x-10">
          <!-- In-Person Sessions -->
          <div class="group">
            <div class="session-card p-8 bg-white rounded-xl border border-[#D2919A]/10 hover:border-[#D2919A]/20 hover:shadow-sm transition-all duration-300 h-full">
              <div class="mb-6 text-center">
                <div class="w-16 h-16 mx-auto rounded-full bg-[#D2919A] flex items-center justify-center shadow-sm transform transition-all duration-300 group-hover:scale-105">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 class="text-2xl font-bold text-[#333333] mt-4">
                  Face-to-Face Sessions
                </h3>
              </div>
              
              <div class="space-y-4">
                <p class="text-[#333333] text-center">
                  Meet in my peaceful therapy room in Poole, Dorset. The space is quiet, private, and designed for your comfort, with easy access and parking available.
                </p>
                
                <div class="space-y-4 mt-6">
                  <div class="flex items-center gap-3">
                    <div class="w-6 h-6 rounded-full bg-[#D2919A]/5 flex items-center justify-center flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-[#D2919A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p class="text-[#333333] text-sm">Comfortable, private setting</p>
                  </div>
                  
                  <div class="flex items-center gap-3">
                    <div class="w-6 h-6 rounded-full bg-[#D2919A]/5 flex items-center justify-center flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-[#D2919A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p class="text-[#333333] text-sm">Easy access and parking</p>
                  </div>
                  
                  <div class="flex items-center gap-3">
                    <div class="w-6 h-6 rounded-full bg-[#D2919A]/5 flex items-center justify-center flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-[#D2919A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p class="text-[#333333] text-sm">50-minute sessions</p>
                  </div>
                </div>
                
                <div class="mt-8 flex justify-center">
                  <a href="#contact" @click.prevent="scrollToContact" class="outline-button inline-flex items-center px-5 py-2.5 text-[#D2919A] border border-[#D2919A]/30 rounded-full text-sm font-medium hover:bg-[#D2919A]/5 transition-colors duration-300">
                    Book Session
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Online Sessions - using the same clean structure -->
          <div class="group">
            <div class="session-card p-8 bg-white rounded-xl border border-[#D2919A]/10 hover:border-[#D2919A]/20 hover:shadow-sm transition-all duration-300 h-full">
              <div class="mb-6 text-center">
                <div class="w-16 h-16 mx-auto rounded-full bg-[#D2919A] flex items-center justify-center shadow-sm transform transition-all duration-300 group-hover:scale-105">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 class="text-2xl font-bold text-[#333333] mt-4">
                  Online Video Sessions
                </h3>
              </div>
              
              <div class="space-y-4">
                <p class="text-[#333333] text-center">
                  Connect via secure video platform from the comfort of your own space. Online sessions offer flexibility and eliminate travel time while maintaining the quality of face-to-face therapy.
                </p>
                
                <div class="space-y-4 mt-6">
                  <div class="flex items-center gap-3">
                    <div class="w-6 h-6 rounded-full bg-[#D2919A]/5 flex items-center justify-center flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-[#D2919A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p class="text-[#333333] text-sm">Secure, encrypted platform</p>
                  </div>
                  
                  <div class="flex items-center gap-3">
                    <div class="w-6 h-6 rounded-full bg-[#D2919A]/5 flex items-center justify-center flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-[#D2919A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p class="text-[#333333] text-sm">No travel required</p>
                  </div>
                  
                  <div class="flex items-center gap-3">
                    <div class="w-6 h-6 rounded-full bg-[#D2919A]/5 flex items-center justify-center flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-[#D2919A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p class="text-[#333333] text-sm">50-minute sessions</p>
                  </div>
                </div>
                
                <div class="mt-8 flex justify-center">
                  <a href="#contact" @click.prevent="scrollToContact" class="outline-button inline-flex items-center px-5 py-2.5 text-[#D2919A] border border-[#D2919A]/30 rounded-full text-sm font-medium hover:bg-[#D2919A]/5 transition-colors duration-300">
                    Book Session
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Telephone Service - using the same clean structure -->
          <div class="group">
            <div class="session-card p-8 bg-white rounded-xl border border-[#D2919A]/10 hover:border-[#D2919A]/20 hover:shadow-sm transition-all duration-300 h-full">
              <div class="mb-6 text-center">
                <div class="w-16 h-16 mx-auto rounded-full bg-[#D2919A] flex items-center justify-center shadow-sm transform transition-all duration-300 group-hover:scale-105">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <h3 class="text-2xl font-bold text-[#333333] mt-4">
                  Telephone Sessions
                </h3>
              </div>
              
              <div class="space-y-4">
                <p class="text-[#333333] text-center">
                  For those who prefer voice-only therapy or have limited internet access. Telephone sessions provide the same therapeutic benefits while allowing you to engage from anywhere.
                </p>
                
                <div class="space-y-4 mt-6">
                  <div class="flex items-center gap-3">
                    <div class="w-6 h-6 rounded-full bg-[#D2919A]/5 flex items-center justify-center flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-[#D2919A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p class="text-[#333333] text-sm">Accessible option</p>
                  </div>
                  
                  <div class="flex items-center gap-3">
                    <div class="w-6 h-6 rounded-full bg-[#D2919A]/5 flex items-center justify-center flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-[#D2919A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p class="text-[#333333] text-sm">Complete privacy</p>
                  </div>
                  
                  <div class="flex items-center gap-3">
                    <div class="w-6 h-6 rounded-full bg-[#D2919A]/5 flex items-center justify-center flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-[#D2919A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p class="text-[#333333] text-sm">50-minute sessions</p>
                  </div>
                </div>
                
                <div class="mt-8 flex justify-center">
                  <a href="#contact" @click.prevent="scrollToContact" class="outline-button inline-flex items-center px-5 py-2.5 text-[#D2919A] border border-[#D2919A]/30 rounded-full text-sm font-medium hover:bg-[#D2919A]/5 transition-colors duration-300">
                    Book Session
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'SessionsSection',
  methods: {
    scrollToContact(e) {
      e.preventDefault();
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }
}
</script>

<style scoped>
/* Session cards with consistent color scheme */
.session-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.03), 0 1px 2px -1px rgba(0, 0, 0, 0.02);
}

.session-card > div:last-child {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.session-card > div:last-child > div:last-child {
  margin-top: auto;
}

.session-card:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
}

/* Primary button styling */
.primary-button {
  transition: all 0.3s ease;
}

.primary-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(210, 145, 154, 0.15);
}

/* Outline button styling */
.outline-button {
  transition: all 0.3s ease;
}

.outline-button:hover {
  transform: translateY(-1px);
}

/* Mobile optimizations */
@media (max-width: 640px) {
  .session-card {
    padding: 1.5rem;
  }
}
</style>